import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { Tournament, Match, Team } from '@/types/cricket';

interface TournamentComparisonProps {
  tournaments: Tournament[];
  matches: Match[];
  teams: Team[];
  selectedTournamentIds: string[];
}

const COLORS = ['#0A5F38', '#3498DB', '#E74C3C', '#F39C12', '#9B59B6', '#1ABC9C', '#34495E', '#E67E22'];

const TournamentComparison: React.FC<TournamentComparisonProps> = ({
  tournaments,
  matches,
  teams,
  selectedTournamentIds,
}) => {
  const selectedTournaments = useMemo(() => {
    return tournaments.filter(t => selectedTournamentIds.includes(t.id));
  }, [tournaments, selectedTournamentIds]);

  const comparisonData = useMemo(() => {
    return selectedTournaments.map(tournament => {
      const tournamentMatches = matches.filter(m => m.tournamentId === tournament.id);
      const totalRuns = tournamentMatches.reduce((sum, match) =>
        sum + (match.team1Score?.runs ?? 0) + (match.team2Score?.runs ?? 0), 0);
      const avgRunsPerMatch = tournamentMatches.length ? (totalRuns / tournamentMatches.length) : 0;

      return {
        name: tournament.name,
        totalMatches: tournamentMatches.length,
        avgRunsPerMatch: parseFloat(avgRunsPerMatch.toFixed(1)),
      };
    });
  }, [selectedTournaments, matches]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tournament Comparison (Matches & Avg Runs)</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedTournaments.length === 0 ? (
            <p className="text-gray-500">Select tournaments from the dropdown above to compare them.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={comparisonData}>
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="totalMatches" name="Total Matches" fill="#0A5F38" />
                <Bar dataKey="avgRunsPerMatch" name="Avg Runs Per Match" fill="#3498DB" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TournamentComparison; 